<script setup lang="ts" generic="T extends string">
// Sierra's .tabs: an underline strip of uppercase tabs, the chosen one underlined in
// the accent, each with an optional mono count; then the chosen panel in the default
// slot. Arrow keys move between tabs (WAI-ARIA tabs, automatic activation).
import { ref, useId } from 'vue';
import type { TabItem } from '../types.ts';

const props = defineProps<{ modelValue: T; tabs: readonly TabItem<T>[] }>();
const emit = defineEmits<{ 'update:modelValue': [value: T] }>();
const id = useId();
const strip = ref<HTMLElement | null>(null);

function onKey(event: KeyboardEvent): void {
  const index = props.tabs.findIndex((tab) => tab.value === props.modelValue);
  const step = { ArrowRight: 1, ArrowLeft: -1 }[event.key];
  let next: number | undefined;
  if (step !== undefined) next = (index + step + props.tabs.length) % props.tabs.length;
  else if (event.key === 'Home') next = 0;
  else if (event.key === 'End') next = props.tabs.length - 1;
  const tab = next === undefined ? undefined : props.tabs[next];
  if (tab === undefined) return;
  event.preventDefault();
  emit('update:modelValue', tab.value);
  strip.value?.querySelectorAll<HTMLElement>('[role="tab"]')[next ?? 0]?.focus();
}
</script>

<template>
  <div class="itc-tabs">
    <div ref="strip" class="itc-tabs__strip" role="tablist" @keydown="onKey">
      <button
        v-for="tab in tabs"
        :id="`${id}-${tab.value}`"
        :key="tab.value"
        type="button"
        role="tab"
        class="itc-tabs__tab"
        :aria-selected="tab.value === modelValue"
        :aria-controls="`${id}-panel`"
        :tabindex="tab.value === modelValue ? 0 : -1"
        @click="emit('update:modelValue', tab.value)"
      >
        {{ tab.label }}<span v-if="tab.count !== undefined" class="itc-tabs__count">{{ tab.count }}</span>
      </button>
    </div>
    <div :id="`${id}-panel`" class="itc-tabs__panel" role="tabpanel" :aria-labelledby="`${id}-${modelValue}`"><slot /></div>
  </div>
</template>

<style scoped>
.itc-tabs__strip {
  display: flex;
  gap: 4px;
  overflow-x: auto;
  border-bottom: 1px solid var(--itc-line);
}
.itc-tabs__tab {
  min-height: var(--itc-tap);
  margin-bottom: -1px;
  padding: 0 16px;
  border: 0;
  border-bottom: 3px solid transparent;
  background: none;
  color: var(--itc-muted);
  font-family: var(--itc-font-display);
  font-size: 15px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  white-space: nowrap;
  cursor: pointer;
}
.itc-tabs__tab[aria-selected='true'] {
  border-bottom-color: var(--itc-accent);
  color: var(--itc-fg);
}
.itc-tabs__count {
  margin-left: 6px;
  color: var(--itc-muted);
  font-family: var(--itc-font-mono);
  font-size: 12px;
  letter-spacing: 0;
}
.itc-tabs__panel {
  margin-top: var(--itc-s4);
}
</style>
